import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import apiClient from '@/utils/api';
import { formatCurrency } from '@/utils/format';
import AdminLayout from '@/components/AdminLayout';
import { Download, FileText, DollarSign, Users, TrendingUp, Receipt, AlertCircle, CalendarDays } from 'lucide-react';

const AdminReports = () => {
  const [stats, setStats] = useState({
    total_members: 0,
    active_members: 0,
    pending_members: 0,
    suspended_members: 0,
    pending_applications: 0
  });
  const [paymentStats, setPaymentStats] = useState({
    currency: 'USD',
    total_invoices: 0,
    paid_invoices_count: 0,
    total_paid_invoices_amount: 0,
    unpaid_invoices_count: 0,
    total_unpaid_invoices_amount: 0,
    overdue_invoices_count: 0,
    total_overdue_amount: 0,
    pending_verification: 0,
    total_revenue: 0
  });
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  // Cycle report state
  const [cycleYear, setCycleYear] = useState(new Date().getFullYear().toString());
  const [cycleReport, setCycleReport] = useState(null);
  
  // Statement export state
  const [statementMemberId, setStatementMemberId] = useState('');
  const [statementType, setStatementType] = useState('annual');
  const [statementYear, setStatementYear] = useState(new Date().getFullYear().toString());
  const [statementMonth, setStatementMonth] = useState('1');
  const [statementQuarter, setStatementQuarter] = useState('1');
  const [downloadingStatement, setDownloadingStatement] = useState(false);

  useEffect(() => {
    loadStats();
    loadMembers();
  }, []);

  useEffect(() => {
    loadCycleReport(cycleYear);
  }, [cycleYear]);

  const loadCycleReport = async (year) => {
    try {
      const res = await apiClient.get(`/membership/cycles/report?year=${year}`);
      setCycleReport(res.data);
    } catch (error) {
      console.error('Failed to load cycle report:', error);
    }
  };

  const loadStats = async () => {
    try {
      const [memberResponse, paymentResponse] = await Promise.all([
        apiClient.get('/reports/members/statistics'),
        apiClient.get('/reports/payments/summary')
      ]);
      setStats(memberResponse.data);
      setPaymentStats(paymentResponse.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
      toast.error('Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  const loadMembers = async () => {
    try {
      const response = await apiClient.get('/admin/members');
      setMembers(response.data);
    } catch (error) {
      console.error('Failed to load members:', error);
    }
  };

  const exportMembersCSV = async () => {
    setExporting(true);
    try {
      const response = await apiClient.get('/reports/members/csv', {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `members_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Members exported successfully!');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const exportFullMembersCSV = async () => {
    setExporting(true);
    try {
      const response = await apiClient.get('/reports/members/csv/full', {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `members_full_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Full member data exported successfully!');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const exportPaymentsCSV = async () => {
    setExporting(true);
    try {
      const response = await apiClient.get('/reports/payments/csv', {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `payments_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Payments exported successfully!');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const downloadStatement = async () => {
    if (!statementMemberId) {
      toast.error('Please select a member');
      return;
    }

    setDownloadingStatement(true);
    try {
      let params = `statement_type=${statementType}&year=${statementYear}`;
      if (statementType === 'monthly') {
        params += `&month=${statementMonth}`;
      } else if (statementType === 'quarterly') {
        params += `&quarter=${statementQuarter}`;
      }

      const response = await apiClient.get(`/statements/download/${statementMemberId}?${params}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename
      let periodLabel = statementYear;
      if (statementType === 'monthly') {
        periodLabel = `${statementYear}_${statementMonth.padStart(2, '0')}`;
      } else if (statementType === 'quarterly') {
        periodLabel = `${statementYear}_Q${statementQuarter}`;
      }
      link.setAttribute('download', `Statement_${statementMemberId}_${statementType}_${periodLabel}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Statement downloaded successfully!');
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Failed to download statement');
    } finally {
      setDownloadingStatement(false);
    }
  };

  // Generate year options (last 5 years + next year)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 7 }, (_, i) => currentYear - 5 + i);

  const monthOptions = [
    { value: '1', label: 'January' },
    { value: '2', label: 'February' },
    { value: '3', label: 'March' },
    { value: '4', label: 'April' },
    { value: '5', label: 'May' },
    { value: '6', label: 'June' },
    { value: '7', label: 'July' },
    { value: '8', label: 'August' },
    { value: '9', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ];

  const quarterOptions = [
    { value: '1', label: 'Q1 (Jan-Mar)' },
    { value: '2', label: 'Q2 (Apr-Jun)' },
    { value: '3', label: 'Q3 (Jul-Sep)' },
    { value: '4', label: 'Q4 (Oct-Dec)' }
  ];

  if (loading) {
    return (
      <AdminLayout currentPage="reports" title="Reports & Analytics">
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  const currency = paymentStats.currency || 'USD';

  return (
    <AdminLayout currentPage="reports" title="Reports & Analytics">
      {/* Member Statistics */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <Users className="w-5 h-5" />
          Member Statistics
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-primary">{stats.total_members}</div>
              <p className="text-sm text-muted-foreground">Total Members</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-green-600">{stats.active_members}</div>
              <p className="text-sm text-muted-foreground">Active</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-yellow-600">{stats.pending_members}</div>
              <p className="text-sm text-muted-foreground">Pending</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-red-600">{stats.suspended_members}</div>
              <p className="text-sm text-muted-foreground">Suspended</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-blue-600">{stats.pending_applications}</div>
              <p className="text-sm text-muted-foreground">Pending Apps</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Membership Cycle Report */}
      <div className="mb-8">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <CalendarDays className="w-5 h-5" />
            Membership Cycle Report
          </h2>
          <div className="w-40">
            <Select value={cycleYear} onValueChange={setCycleYear}>
              <SelectTrigger data-testid="cycle-year-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {yearOptions.map((year) => (
                  <SelectItem key={year} value={year.toString()}>{year} Cycle</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4" data-testid="cycle-report-cards">
          <Card className="bg-green-50 border-green-200">
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-green-700">{cycleReport?.active ?? 0}</div>
              <p className="text-sm text-green-600">Active</p>
            </CardContent>
          </Card>
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-blue-700">{cycleReport?.renewed ?? 0}</div>
              <p className="text-sm text-blue-600">Renewed</p>
            </CardContent>
          </Card>
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-amber-700">{cycleReport?.outstanding ?? 0}</div>
              <p className="text-sm text-amber-600">Outstanding</p>
            </CardContent>
          </Card>
          <Card className="bg-red-50 border-red-200">
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-red-700">{cycleReport?.expired ?? 0}</div>
              <p className="text-sm text-red-600">Expired</p>
            </CardContent>
          </Card>
          <Card className="bg-purple-50 border-purple-200">
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-purple-700">{cycleReport?.new_registrations ?? 0}</div>
              <p className="text-sm text-purple-600">New Registrations</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Payment & Revenue Statistics */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <DollarSign className="w-5 h-5" />
          Payment & Revenue Statistics
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <Card className="bg-green-50 border-green-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="w-5 h-5 text-green-600" />
                <p className="text-sm font-medium text-green-700">Total Revenue</p>
              </div>
              <div className="text-xl sm:text-2xl font-bold text-green-700" data-testid="total-revenue">
                {formatCurrency(paymentStats.total_revenue, currency)}
              </div>
              <p className="text-xs text-green-600 mt-1">From verified payments</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-primary">{paymentStats.total_invoices}</div>
              <p className="text-sm text-muted-foreground">Total Invoices</p>
            </CardContent>
          </Card>
          <Card className="bg-emerald-50 border-emerald-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 mb-1">
                <Receipt className="w-4 h-4 text-emerald-600" />
                <p className="text-sm font-medium text-emerald-700">Paid Invoices</p>
              </div>
              <div className="text-2xl font-bold text-emerald-700">{paymentStats.paid_invoices_count}</div>
              <p className="text-sm text-emerald-600">{formatCurrency(paymentStats.total_paid_invoices_amount, currency)}</p>
            </CardContent>
          </Card>
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 mb-1">
                <AlertCircle className="w-4 h-4 text-amber-600" />
                <p className="text-sm font-medium text-amber-700">Unpaid Invoices</p>
              </div>
              <div className="text-2xl font-bold text-amber-700">{paymentStats.unpaid_invoices_count}</div>
              <p className="text-sm text-amber-600">{formatCurrency(paymentStats.total_unpaid_invoices_amount, currency)}</p>
            </CardContent>
          </Card>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="bg-red-50 border-red-200">
            <CardContent className="pt-6">
              <p className="text-sm font-medium text-red-700">Overdue Invoices</p>
              <div className="text-2xl font-bold text-red-700">{paymentStats.overdue_invoices_count}</div>
              <p className="text-sm text-red-600">{formatCurrency(paymentStats.total_overdue_amount, currency)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground">Pending Verification</p>
              <div className="text-2xl font-bold text-yellow-600">{paymentStats.pending_verification}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground">Currency</p>
              <div className="text-2xl font-bold text-primary">{currency}</div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Statement Export */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <CalendarDays className="w-5 h-5" />
          Member Statements
        </h2>
        <Card className="border-primary">
          <CardHeader>
            <CardTitle className="text-lg">Export Member Statement (PDF)</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Generate financial statements showing opening balance, all transactions, and closing balance for a specific period.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
              {/* Member Select */}
              <div className="sm:col-span-2 lg:col-span-1">
                <Label htmlFor="statement-member">Member</Label>
                <Select value={statementMemberId} onValueChange={setStatementMemberId}>
                  <SelectTrigger id="statement-member" data-testid="statement-member-select">
                    <SelectValue placeholder="Select member" />
                  </SelectTrigger>
                  <SelectContent>
                    {members.map((member) => (
                      <SelectItem key={member.member_id} value={member.member_id}>
                        {member.first_name} {member.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Statement Type */}
              <div>
                <Label htmlFor="statement-type">Type</Label>
                <Select value={statementType} onValueChange={setStatementType}>
                  <SelectTrigger id="statement-type" data-testid="statement-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="annual">Annual</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Year */}
              <div>
                <Label htmlFor="statement-year">Year</Label>
                <Select value={statementYear} onValueChange={setStatementYear}>
                  <SelectTrigger id="statement-year" data-testid="statement-year-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {yearOptions.map((year) => (
                      <SelectItem key={year} value={year.toString()}>
                        {year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Month (for monthly) */}
              {statementType === 'monthly' && (
                <div>
                  <Label htmlFor="statement-month">Month</Label>
                  <Select value={statementMonth} onValueChange={setStatementMonth}>
                    <SelectTrigger id="statement-month" data-testid="statement-month-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {monthOptions.map((month) => (
                        <SelectItem key={month.value} value={month.value}>
                          {month.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Quarter (for quarterly) */}
              {statementType === 'quarterly' && (
                <div>
                  <Label htmlFor="statement-quarter">Quarter</Label>
                  <Select value={statementQuarter} onValueChange={setStatementQuarter}>
                    <SelectTrigger id="statement-quarter" data-testid="statement-quarter-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {quarterOptions.map((quarter) => (
                        <SelectItem key={quarter.value} value={quarter.value}>
                          {quarter.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Download Button */}
              <div className="flex items-end">
                <Button 
                  onClick={downloadStatement} 
                  disabled={downloadingStatement || !statementMemberId}
                  className="w-full"
                  data-testid="download-statement-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {downloadingStatement ? 'Generating...' : 'Download PDF'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Export Options */}
      <div>
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5" />
          Export Data
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Basic Member Export</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Export basic member information including name, email, contact, and status.
              </p>
              <Button onClick={exportMembersCSV} disabled={exporting} className="w-full" data-testid="export-members-csv">
                <Download className="w-4 h-4 mr-2" />
                {exporting ? 'Exporting...' : 'Export Members CSV'}
              </Button>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Full Member Export</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Export complete member data including <strong>next of kin and beneficiaries</strong>.
              </p>
              <Button onClick={exportFullMembersCSV} disabled={exporting} className="w-full" data-testid="export-full-members-csv">
                <Download className="w-4 h-4 mr-2" />
                {exporting ? 'Exporting...' : 'Export Full Data CSV'}
              </Button>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Payment Export</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Export all payment records including verification status.
              </p>
              <Button onClick={exportPaymentsCSV} disabled={exporting} className="w-full" variant="outline" data-testid="export-payments-csv">
                <Download className="w-4 h-4 mr-2" />
                {exporting ? 'Exporting...' : 'Export Payments CSV'}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminReports;
